import { Box } from '@ltvco/refresh-lib/theme';
import { useCallback, type FC } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  COURT_CASES_V2_ROUTES,
  LEGAL_INTELLIGENCE_ROUTES,
  parseLegalIntelSection,
  withLegalIntelSection,
} from '../../../constants';
import { LegalSearchBreadcrumbs } from '../../shared/LegalSearchBreadcrumbs';
import { buildRecallReportUrl } from '../utils';

import { RecallReportResults } from './RecallReportResults';
import { SimilarRecalls } from './SimilarRecalls';
import { useRecallReport } from './useRecallReport';
import { getDetailConfig } from './utils';

import type { NormalizedRecallCard } from '../interfaces';

export const RecallReport: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const section = parseLegalIntelSection(location.search, location.pathname);
  const returnTo =
    searchParams.get('returnTo') ??
    withLegalIntelSection(COURT_CASES_V2_ROUTES.RECALLS, section);
  const { validSource, recallType, matchItem, similarCards, vehicleParams } =
    useRecallReport();

  const handleSimilarCardClick = useCallback(
    (card: NormalizedRecallCard) => {
      navigate(buildRecallReportUrl(card, vehicleParams));
    },
    [navigate, vehicleParams]
  );

  if (!validSource || !recallType) {
    return null;
  }

  const recallTitle = (() => {
    if (!matchItem) {
      return 'Recall';
    }
    const detail = getDetailConfig(matchItem);
    return detail.title || detail.subtitle || 'Recall';
  })();

  return (
    <Box sx={{ width: '100%', px: 2.5, mb: 1 }}>
      <LegalSearchBreadcrumbs
        segments={[
          { label: 'Legal Intelligence', to: LEGAL_INTELLIGENCE_ROUTES.BASE },
          { label: 'Recalls', to: returnTo },
          { label: recallTitle },
        ]}
      />

      <RecallReportResults item={matchItem} />

      <SimilarRecalls
        cards={similarCards}
        onCardClick={handleSimilarCardClick}
      />
    </Box>
  );
};
